import type { CSSProperties } from 'react'
import type { ActivityButtonVisualStyle } from '@/types/activity'

export function toActivityAlphaColor(color?: string, opacity = 1) {
  const normalizedColor = color?.trim()

  if (!normalizedColor) {
    return undefined
  }

  const normalizedOpacity = Math.max(0, Math.min(1, opacity))
  const rawHex = normalizedColor.replace('#', '')
  const hex =
    rawHex.length === 3
      ? rawHex
          .split('')
          .map((char) => char + char)
          .join('')
      : rawHex

  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return normalizedColor
  }

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${normalizedOpacity})`
}

export function hasActivityButtonGradientOverlay(
  action: ActivityButtonVisualStyle
) {
  return Boolean(
    action.top_overlay_color?.trim() ||
    action.bottom_overlay_color?.trim() ||
    typeof action.top_overlay_opacity === 'number' ||
    typeof action.bottom_overlay_opacity === 'number'
  )
}

export function shouldUseActivityButtonGradientOverlay(
  action: ActivityButtonVisualStyle
) {
  if (action.overlay_enabled === false) {
    return false
  }

  if (action.overlay_enabled === true) {
    return true
  }

  return hasActivityButtonGradientOverlay(action)
}

export function shouldUseActivityButtonLegacyOverlay(
  action: ActivityButtonVisualStyle
) {
  if (action.overlay_enabled === false || action.overlay_enabled === true) {
    return false
  }

  return typeof action.background_opacity === 'number'
}

export function shouldApplyActivityButtonOverlay(
  action: ActivityButtonVisualStyle
) {
  return (
    shouldUseActivityButtonGradientOverlay(action) ||
    shouldUseActivityButtonLegacyOverlay(action)
  )
}

export function hasActivityButtonOwnBackground(
  action?: ActivityButtonVisualStyle
) {
  if (!action) {
    return false
  }

  return Boolean(
    action.background_image_url ||
    action.background_color ||
    shouldUseActivityButtonGradientOverlay(action)
  )
}

export function hasActivityButtonOwnSurfaceBackground(
  action?: ActivityButtonVisualStyle
) {
  if (!action) {
    return false
  }

  return Boolean(action.background_image_url || action.background_color)
}

function getPlainActivityButtonBackgroundStyle(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
): CSSProperties | undefined {
  if (action.background_image_url) {
    return {
      backgroundColor: action.background_color || fallbackColor || undefined,
      backgroundImage: `url(${action.background_image_url})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }

  if (action.background_color || fallbackColor) {
    return {
      backgroundColor: action.background_color || fallbackColor,
    }
  }

  return undefined
}

export function getActivityButtonSurfaceStyle(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
) {
  return getPlainActivityButtonBackgroundStyle(action, fallbackColor)
}

function getLegacyActivityButtonBackgroundStyle(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
): CSSProperties | undefined {
  if (action.background_image_url) {
    return {
      backgroundColor: action.background_color || fallbackColor || undefined,
      backgroundImage: `linear-gradient(0deg, rgba(15,23,42,${1 - (action.background_opacity ?? 1)}) 0%, rgba(15,23,42,${1 - (action.background_opacity ?? 1)}) 100%), url(${action.background_image_url})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }

  if (action.background_color || fallbackColor) {
    return {
      backgroundColor: action.background_color || fallbackColor,
    }
  }

  return undefined
}

function getActivityButtonGradientOverlayLayer(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
) {
  const topOverlayColor =
    toActivityAlphaColor(
      action.top_overlay_color ||
        action.background_color ||
        fallbackColor ||
        '#FFFFFF',
      action.top_overlay_opacity ?? action.background_opacity ?? 0.32
    ) || 'rgba(255,255,255,0.32)'
  const bottomOverlayColor =
    toActivityAlphaColor(
      action.bottom_overlay_color ||
        action.background_color ||
        action.top_overlay_color ||
        fallbackColor ||
        '#FFFFFF',
      action.bottom_overlay_opacity ??
        action.background_opacity ??
        action.top_overlay_opacity ??
        0.32
    ) || 'rgba(255,255,255,0.32)'

  return `linear-gradient(180deg, ${topOverlayColor} 0%, ${bottomOverlayColor} 100%)`
}

export function getActivityButtonOverlayStyle(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
): CSSProperties | undefined {
  if (action.overlay_enabled === false) {
    return undefined
  }

  if (shouldUseActivityButtonGradientOverlay(action)) {
    return {
      backgroundImage: getActivityButtonGradientOverlayLayer(
        action,
        fallbackColor
      ),
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }

  if (shouldUseActivityButtonLegacyOverlay(action)) {
    return {
      backgroundColor: `rgba(15,23,42,${Math.max(
        0,
        Math.min(1, 1 - (action.background_opacity ?? 1))
      )})`,
    }
  }

  return undefined
}

export function getActivityButtonBackgroundStyle(
  action: ActivityButtonVisualStyle,
  fallbackColor?: string
): CSSProperties | undefined {
  if (action.overlay_enabled === false) {
    return getPlainActivityButtonBackgroundStyle(action, fallbackColor)
  }

  if (shouldUseActivityButtonGradientOverlay(action)) {
    const gradientLayer = getActivityButtonGradientOverlayLayer(
      action,
      fallbackColor
    )

    return {
      backgroundColor: action.background_color || fallbackColor || undefined,
      backgroundImage: action.background_image_url
        ? `${gradientLayer}, url(${action.background_image_url})`
        : gradientLayer,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }

  if (shouldUseActivityButtonLegacyOverlay(action)) {
    return getLegacyActivityButtonBackgroundStyle(action, fallbackColor)
  }

  return getPlainActivityButtonBackgroundStyle(action, fallbackColor)
}

export function getActivityButtonLabelStyle(
  action: ActivityButtonVisualStyle,
  defaults?: {
    textColor?: string
    fontSize?: number
    fontWeight?: number
  }
): CSSProperties {
  return {
    color: action.text_color || defaults?.textColor || undefined,
    fontSize:
      typeof action.font_size === 'number'
        ? `${action.font_size}px`
        : defaults?.fontSize
          ? `${defaults.fontSize}px`
          : undefined,
    fontWeight:
      typeof action.font_weight === 'number'
        ? action.font_weight
        : defaults?.fontWeight,
  }
}
